import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = getCategoryBySlug(slug || 'southeast-asia');
  const articles = getArticlesByCategory(slug || 'southeast-asia');

  useEffect(() => {
    if (slug && !getCategoryBySlug(slug)) {
      navigate('/category/southeast-asia', { replace: true });
    }
  }, [slug, navigate]);

  const activeCategory = category || categories[0];
  const currentArticles = articles.length > 0 ? articles : getArticlesByCategory('southeast-asia');

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name}旅游目的地 - 亚洲天堂`,
    description: activeCategory.description,
    url: `https://asiaparadise.com/category/${activeCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亚洲天堂',
      url: 'https://asiaparadise.com/',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '目的地' },
              { label: activeCategory.name },
            ]} />
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mt-2">
              {activeCategory.name}旅游目的地
            </h1>
            <p className="text-foreground-600 text-sm md:text-base mt-3 max-w-2xl">
              {activeCategory.description}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-2 bg-background-100 rounded-full p-1 w-fit" role="tablist" aria-label="目的地分类">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                role="tab"
                aria-selected={activeCategory.slug === cat.slug}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory.slug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {currentArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group block cursor-pointer"
              >
                <article className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-secondary-200 transition-all duration-300 h-full flex flex-col">
                  <div className="relative w-full h-[200px] md:h-[220px] overflow-hidden">
                    <img
                      src={article.image}
                      alt={`${article.title} - 亚洲天堂旅行攻略`}
                      title={`${article.title} | 亚洲天堂`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 md:p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">
                        {article.destinationName}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-secondary-600 transition-colors line-clamp-2 leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-foreground-600 text-xs md:text-sm mt-2 line-clamp-2 leading-relaxed flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-background-200/50 flex-wrap">
                      {article.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs bg-background-100 text-foreground-500 px-2 py-0.5 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>

          {currentArticles.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-file-list-3-line text-4xl text-foreground-300"></i>
              <p className="text-foreground-500 mt-4">该分类暂无文章，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}